import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import ToggleButton from '@mui/material/ToggleButton';
import Link from '@mui/material/Link';
import styled from '@emotion/styled';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import { MEDIA_TYPE } from '../../../../../util/deals-util';
import { Field, useFormikContext } from 'formik';
import { ToggleButtonGroup } from 'formik-mui';
import { AdFormat } from '../../../../../api/services/dto/ad-format';
import { MouseEvent } from 'react';
import { getColor } from '../../../../../theme/colors/colors';
import { FormHelperText } from '@mui/material';
import HelpIcon from '@mui/icons-material/HelpOutline';
import Tooltip from '@mui/material/Tooltip';

type MediaCardProps = {
  adFormats: AdFormat[];
  onMediaTypeChange: (newMediaType: MEDIA_TYPE) => void;
  setCustomDirtyState: () => void;
  helperText: boolean;
};

const SPONSORED_POST_URL: string =
  'https://www.cwire.com/en/advertising/managed-services/managed-services-resources#sponsoredpost';
const SPONSORED_VIDEO_URL: string =
  'https://www.cwire.com/en/advertising/managed-services/managed-services-resources#sponsoredvideo';
const OUTSTREAM_VIDEO_URL: string =
  'https://www.cwire.com/en/advertising/managed-services/managed-services-resources#outstreamvideo';
const STORY_AD_URL: string = 'https://www.cwire.com/en/advertising/managed-services/managed-services-resources#story';
const FLIP_AD_URL: string = 'https://www.cwire.com/en/advertising/managed-services/managed-services-resources#flip';
const IMAGE_SHOWCASE_AD_URL: string =
  'https://www.cwire.com/en/advertising/managed-services/managed-services-resources#image-showcase';
const VIDEO_SHOWCASE_AD_URL: string = 'https://www.cwire.com/en/advertising/managed-services/managed-services-resources#video-showcase';

const MediaCard = ({
  adFormats,
  onMediaTypeChange,
  setCustomDirtyState,
  helperText,
}: MediaCardProps) => {
  const { setFieldValue, validateField, values } = useFormikContext();

  const handleMediaTypeChange = async (newMediaType: MEDIA_TYPE) => {
    await setFieldValue('adFormats', []);
    validateField('adFormats');
    onMediaTypeChange(newMediaType);
    setCustomDirtyState();
  };

  const pickLinkForAdFormat = (adFormat: AdFormat) => {
    switch (adFormat.mediaType) {
      case MEDIA_TYPE.NATIVE:
        switch (adFormat.name) {
          case 'Sponsored Post':
            return SPONSORED_POST_URL;
          default:
            return SPONSORED_VIDEO_URL;
        }
      case MEDIA_TYPE.VIDEO:
        return OUTSTREAM_VIDEO_URL;
      case MEDIA_TYPE.CWIRE_FORMAT:
        switch (adFormat.name) {
          case 'Story Ad (Image)': case 'Story Ad (Video)':
            return STORY_AD_URL;
          case 'Flip Ad':
            return FLIP_AD_URL;
          case 'Image Showcase Ad':
            return IMAGE_SHOWCASE_AD_URL;
          case 'Video Showcase Ad':
            return VIDEO_SHOWCASE_AD_URL;
          default:
            return SPONSORED_VIDEO_URL;
        }

    }
  }

  const handleAdFormatChange = (_event: MouseEvent<HTMLElement>, newValue: any) => {
    if (adFormats?.length && adFormats[0].mediaType === MEDIA_TYPE.CWIRE_FORMAT) {
      // For C Wire formats, keep only the last selected value as an array
      setFieldValue('adFormats', Array.isArray(newValue) ? [newValue[newValue.length - 1]] : [newValue]);
    } else {
      // For other formats, allow multiple selection
      setFieldValue('adFormats', newValue);
    }
    setCustomDirtyState();
  };

  return (
    <Card elevation={4} sx={{ marginBottom: '32px' }}>
      <CardContent>
        <CardTitle>Media</CardTitle>

        <MediaTypeAndAdFormatsWrapper>
          <>
            <FieldLabel>Media Type</FieldLabel>
            <Field
              component={ToggleButtonGroup}
              exclusive
              size="small"
              name="mediaType"
              type="checkbox"
            >
              <ToggleButton
                value={MEDIA_TYPE.BANNER}
                aria-label="banner"
                onClick={(e: MouseEvent<HTMLElement>, newValue) => handleMediaTypeChange(newValue)}
              >
                Banner
              </ToggleButton>
              <ToggleButton
                value={MEDIA_TYPE.NATIVE}
                aria-label="native"
                onClick={(e: MouseEvent<HTMLElement>, newValue) => handleMediaTypeChange(newValue)}
              >
                Native
              </ToggleButton>
              <ToggleButton
                value={MEDIA_TYPE.VIDEO}
                aria-label="video"
                onClick={(e: MouseEvent<HTMLElement>, newValue) => handleMediaTypeChange(newValue)}
              >
                Video
              </ToggleButton>
              <ToggleButton
                value={MEDIA_TYPE.CWIRE_FORMAT}
                aria-label="cwire_format"
                onClick={(e: MouseEvent<HTMLElement>, newValue) =>
                  handleMediaTypeChange(newValue)}
              >
                C Wire Formats (<span style={{ textTransform: 'lowercase' }}>β</span>eta)
              </ToggleButton>
            </Field>
          </>
          <>
            <FieldLabel>Ad Formats&nbsp;
              {adFormats?.length && adFormats[0].mediaType === MEDIA_TYPE.CWIRE_FORMAT && (
                <BetaPreviewLink
                  rel="noopener noreferrer"
                  href="https://www.cwire.com/post/call-for-beta-testers-be-the-first-to-access-c-wires-new-programmatic-ad-formats"
                  target="_blank"
                  underline="none"
                  sx={{ display: 'inline-flex', alignItems: 'center' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span style={{ textTransform: 'lowercase' }}>β</span>eta
                  <Tooltip title="Learn more about C Wire beta formats" placement="top-start" arrow>
                    <HelpIcon
                    sx={{ fontSize: '1.2rem', height: '0.9rem', width: '0.9rem', color: getColor('primary'), marginLeft: '2px' }}
                    titleAccess="Learn more about C Wire beta formats"
                    />
                  </Tooltip>
                  
                </BetaPreviewLink>
              )}
            </FieldLabel>
            <Field
              component={ToggleButtonGroup}
              name="adFormats"
              type="checkbox"
              className={
                adFormats?.length && adFormats[0].mediaType === MEDIA_TYPE.BANNER
                  ? 'ad-formats-toggle-button-group banner'
                  : 'ad-formats-toggle-button-group'
              }
              onChange={handleAdFormatChange}
            >
              {adFormats.map((adFormat: AdFormat) => {
                return (
                  <ToggleButton
                    disableRipple
                    key={adFormat.id}
                    value={adFormat.id}
                    aria-label={adFormat.name}
                    className={adFormat.mediaType === MEDIA_TYPE.CWIRE_FORMAT ? "ad-formats-toggle-button cwire" : "ad-formats-toggle-button"}
                  >
                    <IconAndTextWrapper
                      style={{
                        paddingTop: '5px',
                        alignSelf: "baseline"
                      }}>
                      {adFormat.mediaType === MEDIA_TYPE.BANNER || adFormat.mediaType === MEDIA_TYPE.CWIRE_FORMAT ? (
                        <PhotoOutlinedIcon />
                      ) : (
                        <SpaceDashboardOutlinedIcon />
                      )}
                      <ButtonText>{adFormat.name}</ButtonText>
                    </IconAndTextWrapper>

                    {
                      adFormat.mediaType !== MEDIA_TYPE.BANNER ? (
                        <PreviewLink
                          rel="noopener noreferrer"
                          href={
                            pickLinkForAdFormat(adFormat)
                          }
                          target="_blank"
                          underline="none"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Ad Specs
                        </PreviewLink>
                      ) : null
                    }
                  </ToggleButton>
                );
              })}
              <FormHelperText error={helperText}>
                {helperText && 'At least one ad format is required'}
              </FormHelperText>
            </Field>
          </>
        </MediaTypeAndAdFormatsWrapper>
      </CardContent>
    </Card >
  );
};

export default MediaCard;

const CardTitle = styled.h3`
  margin-bottom: 8px;
`;

const MediaTypeAndAdFormatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const FieldLabel = styled.label`
  margin: 16px 0 8px;
`;

const IconAndTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ButtonText = styled.div`
  font-size: 13px;
`;

const PreviewLink = styled(Link)`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: ${getColor('primary')};
  position: absolute;
  top: 94px;
  left: 0;
`;

const BetaPreviewLink = styled(Link)`
  color: ${getColor('primary')};
`;

